import { useCallback, useEffect, useMemo, useState } from "react";
import Table from "../../../Components/Table/Table";
import ModalSimple from "../../../Components/ModalSimple";
import StatusColorful from "../../../Components/Table/Components/StatusColorful";
import { formatTradePrice } from "../../../helper/formatTradePrice";
import { isoToJalali } from "../../../helper/isoToJalali";
import PayoutDecisionModal from "./PayoutDecisionModal";
import { API_BASE_URL } from "../../../config/api";

const PARTICIPATION_BASE_PATH = `${API_BASE_URL}/api/v0/payment-payout/admin`;
const PAYOUT_REQUESTS_ENDPOINT = `${PARTICIPATION_BASE_PATH}/referrals/payouts/requests/`;
const PAYOUT_BY_CODE_ENDPOINT = `${PARTICIPATION_BASE_PATH}/referrals/payouts/`;

const STATUS_FILTERS = [
  { key: "all", label: "همه" },
  { key: "pending", label: "در انتظار" },
  { key: "completed", label: "تکمیل شده" },
  { key: "rejected", label: "رد شده" },
];

function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

function getErrorMessageFromPayload(payload, fallbackText) {
  if (!payload) return fallbackText;
  if (typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail;
  }

  const firstArrayValue = Object.values(payload).find(
    (value) => Array.isArray(value) && value.length > 0,
  );
  if (firstArrayValue && typeof firstArrayValue[0] === "string") {
    return firstArrayValue[0];
  }

  return fallbackText;
}

function formatDate(value) {
  if (!value) return "-";
  try {
    return isoToJalali(value);
  } catch {
    return String(value);
  }
}

function statusText(status) {
  if (status === "pending") return "در انتظار";
  if (status === "completed") return "تکمیل شده";
  if (status === "rejected") return "رد شده";
  if (status === "approved") return "تایید شده";
  return status || "-";
}

function StatusCell({ value }) {
  if (value === "completed") {
    return (
      <StatusColorful
        SVGName="tick"
        text="تکمیل شده"
        className="w-24"
        color="green"
      />
    );
  }

  if (value === "pending") {
    return (
      <StatusColorful
        SVGName="stall"
        text="در انتظار"
        className="w-24"
        color="yellow"
      />
    );
  }

  if (value === "rejected") {
    return (
      <StatusColorful
        SVGName="failed"
        text="رد شده"
        className="w-24"
        color="red"
      />
    );
  }

  return statusText(value);
}

export default function PayoutTab({ authFetch }) {
  const [activeStatus, setActiveStatus] = useState("all");

  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [listError, setListError] = useState("");

  const [selectedRequestCode, setSelectedRequestCode] = useState("");
  const [selectedRequestDetail, setSelectedRequestDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const [actionError, setActionError] = useState("");
  const [actionSuccess, setActionSuccess] = useState("");
  const [decisionSubmitting, setDecisionSubmitting] = useState(false);
  const [showDecisionModal, setShowDecisionModal] = useState(false);

  const fetchRequests = useCallback(async () => {
    setRequestsLoading(true);
    setListError("");
    setActionError("");

    try {
      const endpoint =
        activeStatus === "all"
          ? PAYOUT_REQUESTS_ENDPOINT
          : `${PAYOUT_REQUESTS_ENDPOINT}?status=${activeStatus}`;

      const response = await authFetch(endpoint, { method: "GET" });
      if (!response) return;
      if (!response.ok) {
        throw new Error(`payout-requests-fetch-failed-${response.status}`);
      }

      const payload = await response.json();
      setRequests(normalizeList(payload));
    } catch (error) {
      console.error("Error fetching payout requests:", error);
      setListError("بارگذاری لیست درخواست‌ها با خطا مواجه شد.");
      setRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  }, [activeStatus, authFetch]);

  const fetchRequestDetail = useCallback(
    async (requestCode) => {
      if (!requestCode) {
        setSelectedRequestDetail(null);
        return;
      }

      setDetailLoading(true);
      setDetailError("");

      try {
        const response = await authFetch(
          `${PAYOUT_BY_CODE_ENDPOINT}${requestCode}/`,
          {
            method: "GET",
          },
        );

        if (!response) return;
        if (!response.ok) {
          throw new Error(`payout-detail-fetch-failed-${response.status}`);
        }

        const payload = await response.json();
        setSelectedRequestDetail(payload);
      } catch (error) {
        console.error("Error fetching payout detail:", error);
        setDetailError("بارگذاری جزئیات برداشت با خطا مواجه شد.");
        setSelectedRequestDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [authFetch],
  );

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    if (requests.length === 0) {
      setSelectedRequestCode("");
      setSelectedRequestDetail(null);
      return;
    }

    const hasSelected = requests.some(
      (request) => String(request.code) === String(selectedRequestCode),
    );

    if (!hasSelected) {
      setSelectedRequestCode(String(requests[0].code));
    }
  }, [requests, selectedRequestCode]);

  useEffect(() => {
    fetchRequestDetail(selectedRequestCode);
  }, [fetchRequestDetail, selectedRequestCode]);

  const handleDecidePayout = async ({ status, transaction_id }) => {
    const payoutId = selectedRequestDetail?.id;
    if (!payoutId) {
      setActionError("شناسه درخواست برداشت یافت نشد.");
      return;
    }

    setActionError("");
    setActionSuccess("");
    setDecisionSubmitting(true);

    try {
      const body = { status };
      if (transaction_id) {
        body.transaction_id = transaction_id;
      }

      const response = await authFetch(
        `${PARTICIPATION_BASE_PATH}/referrals/payouts/${payoutId}/decision/`,
        {
          method: "POST",
          body: JSON.stringify(body),
        },
      );

      if (!response) return;

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        setActionError(
          getErrorMessageFromPayload(payload, "ثبت تصمیم با خطا مواجه شد."),
        );
        return;
      }

      setActionSuccess("تصمیم برداشت با موفقیت ثبت شد.");
      setShowDecisionModal(false);

      await Promise.all([
        fetchRequests(),
        fetchRequestDetail(selectedRequestCode),
      ]);
    } catch (error) {
      console.error("Error deciding payout request:", error);
      setActionError("ثبت تصمیم با خطا مواجه شد.");
    } finally {
      setDecisionSubmitting(false);
    }
  };

  const tableData = useMemo(() => {
    return requests.map((request) => ({
      id: request.id ?? "-",
      codeRaw: request.code,
      code: request.code ?? "-",
      createdAt: formatDate(request.created_at),
      updatedAt: formatDate(request.updated_at),
      user: request.user_full_name || request.user_email || "-",
      amount: formatTradePrice(request.amount),
      walletAddress: request.wallet_address || "-",
      network: request.network || "-",
      status: request.status || "-",
    }));
  }, [requests]);

  return (
    <div className="w-full min-h-0 flex flex-col gap-2.5">
      <div className="w-full p-5 rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark border border-zi-border-light dark:border-zi-border-dark flex flex-col gap-4">
        <div className="w-full flex flex-wrap items-center gap-2">
          {STATUS_FILTERS.map((statusItem) => (
            <button
              key={statusItem.key}
              type="button"
              onClick={() => setActiveStatus(statusItem.key)}
              className={`rounded-md px-3 py-1.5 text-sm border ${
                activeStatus === statusItem.key
                  ? "border-zi-blue-light text-zi-blue-light"
                  : "border-zi-border-light dark:border-zi-border-dark text-zi-text-light/70 dark:text-zi-text-dark/70"
              }`}
            >
              {statusItem.label}
            </button>
          ))}
        </div>

        {listError && <p className="text-red-500 text-sm">{listError}</p>}
        {actionError && <p className="text-red-500 text-sm">{actionError}</p>}
        {actionSuccess && (
          <p className="text-green-500 text-sm">{actionSuccess}</p>
        )}

        {requestsLoading && (
          <p className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
            در حال بارگذاری درخواست‌ها...
          </p>
        )}

        <Table
          columns={[
            { key: "id", label: "شناسه" },
            { key: "code", label: "کد درخواست" },
            { key: "createdAt", label: "تاریخ ایجاد" },
            { key: "updatedAt", label: "تاریخ بروزرسانی" },
            { key: "user", label: "کاربر" },
            { key: "amount", label: "مبلغ" },
            { key: "network", label: "شبکه" },
            { key: "walletAddress", label: "آدرس مقصد" },
            {
              key: "status",
              label: "وضعیت",
              render: (value) => <StatusCell value={value} />,
            },
            {
              key: "detail",
              label: "جزئیات",
              render: (_, row) => (
                <button
                  type="button"
                  onClick={() => setSelectedRequestCode(String(row.codeRaw))}
                  className="rounded-md px-2 py-1 text-xs border border-zi-blue-light text-zi-blue-light"
                >
                  مشاهده
                </button>
              ),
            },
          ]}
          data={tableData}
        />
      </div>

      <div className="w-full p-5 rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark border border-zi-border-light dark:border-zi-border-dark flex flex-col gap-4">
        <p className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
          جزئیات درخواست انتخاب‌شده
        </p>

        {detailLoading && (
          <p className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
            در حال بارگذاری جزئیات...
          </p>
        )}

        {!detailLoading && detailError && (
          <p className="text-red-500 text-sm">{detailError}</p>
        )}

        {!detailLoading && !detailError && !selectedRequestDetail && (
          <p className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
            برای مشاهده جزئیات، یک درخواست را انتخاب کنید.
          </p>
        )}

        {!detailLoading && !detailError && selectedRequestDetail && (
          <>
            <div className="w-full rounded-xl border border-zi-border-light dark:border-zi-border-dark overflow-hidden">
              {[
                { label: "کد", value: selectedRequestDetail.code || "-" },
                {
                  label: "کاربر",
                  value:
                    selectedRequestDetail.user_full_name ||
                    selectedRequestDetail.user_email ||
                    "-",
                },
                {
                  label: "مبلغ",
                  value: formatTradePrice(selectedRequestDetail.amount),
                },
                {
                  label: "شبکه",
                  value: selectedRequestDetail.network || "-",
                },
                {
                  label: "آدرس مقصد",
                  value: selectedRequestDetail.wallet_address || "-",
                },
                {
                  label: "تاریخ ایجاد",
                  value: formatDate(selectedRequestDetail.created_at),
                },
                {
                  label: "آخرین بروزرسانی",
                  value: formatDate(selectedRequestDetail.updated_at),
                },
                {
                  label: "وضعیت",
                  value: statusText(selectedRequestDetail.status),
                },
              ].map((item) => (
                <div
                  key={item.label}
                  className="w-full flex items-center gap-2 border-b border-zi-border-light dark:border-zi-border-dark last:border-b-0"
                >
                  <div className="w-40 shrink-0 px-3 py-2 text-sm text-zi-text-light/60 dark:text-zi-text-dark/60 bg-zi-bg1-light dark:bg-zi-bg1-dark">
                    {item.label}
                  </div>
                  <div className="px-3 py-2 text-sm text-zi-text-light dark:text-zi-text-dark break-all">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>

            {selectedRequestDetail.status === "pending" && (
              <button
                type="button"
                onClick={() => setShowDecisionModal(true)}
                className="w-full rounded-lg py-2 text-sm text-zi-text-dark bg-zi-gradient-button-blue"
              >
                بررسی و تصمیم‌گیری
              </button>
            )}
          </>
        )}
      </div>

      {showDecisionModal && selectedRequestDetail && (
        <ModalSimple
          showModal={showDecisionModal}
          setShowModal={setShowDecisionModal}
        >
          <PayoutDecisionModal
            payoutData={selectedRequestDetail}
            onClose={() => setShowDecisionModal(false)}
            onSubmit={handleDecidePayout}
            submitting={decisionSubmitting}
          />
        </ModalSimple>
      )}
    </div>
  );
}
